import JSZip from "jszip";

export function isDocx(file) {
  return file && file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
}

export function isPdf(file) {
  return file && file.type === 'application/pdf';
}

export async function validateFileSize(file, maxSizeMB = 2) {
  const maxBytes = maxSizeMB * 1024 * 1024

  if (!file) {
    return { ok: false, message: "No file selected" }
  }

  if (file.size === 0) {
    return { ok: false, message: "File is empty (0 bytes)" }
  }

  if (file.size > maxBytes) {
    return { 
      ok: false, 
      message: `Maximum allowed file size is ${maxSizeMB} MB` 
    }
  }

  const DOCX_TYPE =
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
  const PDF_TYPE = "application/pdf"

  if (file.type === PDF_TYPE || file.name.toLowerCase().endsWith(".pdf")) {
    try {
      // Minimum safe size for PDF
      if (file.size < 1000) {
        return { ok: false, message: "PDF file is too small or empty" }
      }

      // Check PDF header
      const headerBuffer = await file.slice(0, 5).arrayBuffer()
      const headerText = new TextDecoder().decode(headerBuffer)

      if (headerText !== "%PDF-") {
        return { ok: false, message: "Invalid or corrupted PDF file" }
      }

      // Optional: Check EOF (extra safety)
      const tailBuffer = await file.slice(file.size - 5, file.size).arrayBuffer()
      const tailText = new TextDecoder().decode(tailBuffer)

      if (!tailText.includes("EOF")) {
        return { ok: false, message: "Corrupted PDF file" }
      }

      return { ok: true }
    } catch (error) {
      return { ok: false, message: "Unable to read PDF file" }
    }
  }

  if (file.type === DOCX_TYPE || file.name.toLowerCase().endsWith(".docx")) {
    try {
      // Minimum safe size for docx
      if (file.size < 8000) {
        return { ok: false, message: "DOCX file is too small and likely empty" }
      }

      const arrayBuffer = await file.arrayBuffer()
      const zip = await JSZip.loadAsync(arrayBuffer)

      const documentXmlFile = zip.file("word/document.xml")

      if (!documentXmlFile) {
        return { ok: false, message: "Invalid DOCX file structure" }
      }

      const documentXml = await documentXmlFile.async("text")

      const textContent = documentXml
        .replace(/<[^>]*>/g, "")
        .replace(/\s+/g, " ")
        .trim()

      if (!textContent || textContent.length < 20) {
        return { ok: false, message: "DOCX file contains no real content" }
      }

      return { ok: true }
    } catch (error) {
      return { ok: false, message: "Corrupted DOCX file" }
    }
  }

  return {
    ok: false,
    message: "Only PDF and DOCX files are allowed"
  }
}

export function formatToLocaleString(dateStr) {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  return d.toLocaleString();
}
  
export function formatAdminActivityDate(input) {
  const alreadyFormatted =
    /^[A-Za-z]{3}\s\d{1,2},\s\d{4}\s\d{1,2}:\d{2}\s(am|pm)$/i;

  if (alreadyFormatted.test(input)) {
    return input;
  }

  const [datePart, timePart] = input.split(', ');
  const [day, month, year] = datePart.split('/').map(Number);
  const [time, meridiem] = timePart.split(' ');
  const [hours, minutes] = time.split(':');

  const date = new Date(
    year,
    month - 1,
    day,
    meridiem.toLowerCase() === 'pm' && hours !== '12'
      ? Number(hours) + 12
      : meridiem.toLowerCase() === 'am' && hours === '12'
      ? 0
      : Number(hours),
    Number(minutes)
  );

  const formatted = date.toLocaleString('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });

  return formatted.replace(',', '').replace(' AM', ' am').replace(' PM', ' pm');
}
  
export function formatHolidayDate(dateStr, oneTime = false, numberOfDays = 1) {
  if (!dateStr) return "-";

  const start = new Date(dateStr);
  if (isNaN(start.getTime())) return "-";

  const startMonth = start.toLocaleDateString("en-US", { month: "long" });
  const startDay = start.getDate();
  const year = start.getFullYear();

  if (numberOfDays === 1) {
    return oneTime 
      ? `${startMonth} ${startDay}, ${year}` 
      : `${startMonth} ${startDay}`;
  }

  const end = new Date(start);
  end.setDate(start.getDate() + numberOfDays - 1);

  const endMonth = end.toLocaleDateString("en-US", { month: "long" });
  const endDay = end.getDate();

  if (startMonth === endMonth) {
    return oneTime 
      ? `${startMonth} ${startDay} - ${endDay}, ${year}` 
      : `${startMonth} ${startDay} - ${endDay}`;
  } else {
    return oneTime 
      ? `${startMonth} ${startDay} - ${endMonth} ${endDay}, ${year}` 
      : `${startMonth} ${startDay} - ${endMonth} ${endDay}`;
  }
}



// WEBPACK FOOTER //
// ./src/utils/fileUtils.js